import {
  type KeyboardEvent,
  type RefObject,
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useState,
} from "react";
import { queries } from "../../shared/queries";
import { isSupplierAvailableFromGrants } from "../../shared/rbac/crossResourceAvailability";
import { type SkillItem } from "../components/Chat/SlashMenu";
import {
  activeSlashToken,
  slashCompletionSuffix,
  type SlashToken,
} from "../lib/slashToken";
import { useGroupMemberships } from "./useGroupMemberships";
import { DEFERRED, useQuery } from "./useQuery";

export type { SkillItem };

export type SlashSkillCompletionState = {
  token: SlashToken | null;
  matches: readonly SkillItem[];
  selectedIndex: number;
  caret: number;
  suffix: string;
  onSelect: (skill: SkillItem) => void;
};

type UseSlashSkillsParams = {
  spaceId: string | null | undefined;
  textareaRef: RefObject<HTMLTextAreaElement | null>;
  value: string;
  setValue: (v: string) => void;
  onAfterAccept?: () => void;
  enabled?: boolean;
};

export function useSlashSkills({
  spaceId,
  textareaRef,
  value,
  setValue,
  onAfterAccept,
  enabled = true,
}: UseSlashSkillsParams) {
  const [slashToken, setSlashToken] = useState<SlashToken | null>(null);

  const [allSkills] = useQuery(
    enabled ? queries.skillsIndex() : undefined,
    DEFERRED
  );
  const [spaceGrants] = useQuery(
    enabled && spaceId
      ? queries.roleGrantsByScope({ scopeId: spaceId })
      : undefined,
    DEFERRED
  );
  const { userGroupIds: groupMemberships } = useGroupMemberships({ enabled });
  const availableSkills = useMemo<SkillItem[]>(() => {
    const enabledSkills = (allSkills ?? []).filter((s) => s.enabled);
    // Mirror what the AI can load: inside a space, only skills the space can use;
    // elsewhere, any skill the user can read (skillsIndex already enforces that).
    const usable =
      spaceId && spaceGrants
        ? enabledSkills.filter((s) =>
            isSupplierAvailableFromGrants(
              "space_uses_skills",
              spaceGrants,
              s.roleGrantsAsScope,
              groupMemberships
            )
          )
        : enabledSkills;
    return usable.map((s) => ({
      id: s.id,
      name: s.name,
      description: s.description,
    }));
  }, [allSkills, spaceId, spaceGrants, groupMemberships]);

  const [selectedSkills, setSelectedSkills] = useState<SkillItem[]>([]);
  const addSkill = useCallback((skill: SkillItem) => {
    setSelectedSkills((prev) =>
      prev.some((s) => s.id === skill.id) ? prev : [...prev, skill]
    );
  }, []);
  const removeSkill = useCallback((id: string) => {
    setSelectedSkills((prev) => prev.filter((s) => s.id !== id));
  }, []);
  const clearSkills = useCallback(() => setSelectedSkills([]), []);

  const syncSlashToken = useCallback((el: HTMLTextAreaElement) => {
    const next =
      el.value === ""
        ? null
        : activeSlashToken(el.value, el.selectionStart ?? 0);
    // Keep the same reference when the token is unchanged so arrow-key
    // navigation (which fires keyup) doesn't churn the highlight.
    setSlashToken((prev) =>
      prev &&
      next &&
      prev.start === next.start &&
      prev.end === next.end &&
      prev.query === next.query
        ? prev
        : next
    );
  }, []);

  // Draft restore never fires input events; sync once the textarea is mounted
  // (and again if the hook becomes enabled after load).
  useLayoutEffect(() => {
    const el = textareaRef.current;
    if (!enabled || !el) return;
    syncSlashToken(el);
  }, [enabled, syncSlashToken, textareaRef]);

  const acceptSlashSkill = useCallback(
    (skill: SkillItem) => {
      if (!slashToken) return;
      const before = value.slice(0, slashToken.start);
      const after = value.slice(slashToken.end);
      // Add a trailing space only at the end of the input; mid-message there's already whitespace after the token.
      const inserted = `/${skill.name}${after === "" ? " " : ""}`;
      const caret = before.length + inserted.length;
      setValue(before + inserted + after);
      addSkill(skill);
      setSlashToken(null);
      requestAnimationFrame(() => {
        const el = textareaRef.current;
        if (el) {
          el.focus();
          el.setSelectionRange(caret, caret);
        }
        onAfterAccept?.();
      });
    },
    [value, slashToken, addSkill, textareaRef, setValue, onAfterAccept]
  );

  const slashMatch =
    slashToken && slashToken.query.length > 0
      ? (availableSkills.find((s) =>
          s.name.toLowerCase().startsWith(slashToken.query.toLowerCase())
        ) ?? null)
      : null;
  const slashCaret = slashToken
    ? slashToken.start + 1 + slashToken.query.length
    : 0;

  // Skills matching a mid-message "/" token, for the caret popover. A token at
  // the start of the input belongs to the initial command menu instead.
  const slashMenuMatches = useMemo<SkillItem[]>(() => {
    if (!slashToken || slashToken.start === 0) return [];
    const query = slashToken.query.toLowerCase();
    return availableSkills.filter((s) =>
      s.name.toLowerCase().startsWith(query)
    );
  }, [availableSkills, slashToken]);

  const [slashSelected, setSlashSelected] = useState(0);
  // Reset the highlight when the token itself changes (typing or moving), but
  // not when arrow keys only move the selection within a stable token.
  useEffect(() => {
    // oxlint-disable-next-line react/react-compiler -- blocks React Compiler; remove when fixed
    setSlashSelected(0);
  }, [slashToken?.start, slashToken?.query]);
  const slashSelectedIndex =
    slashMenuMatches.length > 0
      ? Math.min(slashSelected, slashMenuMatches.length - 1)
      : 0;

  // The popover drives the inline completion when it's open; otherwise the top
  // prefix match does (type-forward for a slash at the start of the input).
  const ghostTarget =
    slashMenuMatches.length > 0
      ? slashMenuMatches[slashSelectedIndex]!
      : slashMatch;
  const slashGhost = slashCompletionSuffix(
    slashToken,
    slashCaret,
    ghostTarget?.name ?? null
  );

  const handleSlashKeyDown = useCallback(
    (e: KeyboardEvent<HTMLTextAreaElement>): boolean => {
      const atEnd =
        e.currentTarget.selectionStart === e.currentTarget.selectionEnd &&
        e.currentTarget.selectionStart === slashToken?.end;
      // Mid-message popover: arrow to navigate, Enter/Tab to accept.
      if (slashMenuMatches.length > 0) {
        if (e.key === "ArrowDown") {
          e.preventDefault();
          setSlashSelected((i) => Math.min(i + 1, slashMenuMatches.length - 1));
          return true;
        }
        if (e.key === "ArrowUp") {
          e.preventDefault();
          setSlashSelected((i) => Math.max(i - 1, 0));
          return true;
        }
        const selected = slashMenuMatches[slashSelectedIndex]!;
        if (e.key === "Enter" || e.key === "Tab") {
          e.preventDefault();
          acceptSlashSkill(selected);
          return true;
        }
        if (e.key === "ArrowRight" && atEnd) {
          e.preventDefault();
          acceptSlashSkill(selected);
          return true;
        }
        return false;
      }
      // Slash at the start of the input: accept the type-forward ghost.
      if (!(slashToken && slashMatch && slashGhost)) return false;
      if (e.key === "Tab") {
        e.preventDefault();
        acceptSlashSkill(slashMatch);
        return true;
      }
      if (e.key === "ArrowRight" && atEnd) {
        e.preventDefault();
        acceptSlashSkill(slashMatch);
        return true;
      }
      return false;
    },
    [
      slashMenuMatches,
      slashSelectedIndex,
      slashToken,
      slashMatch,
      slashGhost,
      acceptSlashSkill,
    ]
  );

  return {
    availableSkills,
    selectedSkills,
    addSkill,
    removeSkill,
    clearSkills,
    syncSlashToken,
    slashMatch,
    slashCompletion: {
      token: slashToken,
      matches: slashMenuMatches,
      selectedIndex: slashSelectedIndex,
      caret: slashCaret,
      suffix: slashGhost,
      onSelect: acceptSlashSkill,
    } satisfies SlashSkillCompletionState,
    handleSlashKeyDown,
  };
}
